import { Collapse } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import type { Workflow } from "@nens/rana/api-v1";
import { RanaTable } from "@nens/ranalib";
import { useTableData } from "@nens/react/hooks";

import { LibraryCard } from "@/components/library-card";
import { SMALL_TILE_CARD_HEIGHT } from "@/components/small-tile-card";
import { useIconSrc } from "@/hooks/use-icon-src";
import { nameColumnString } from "@/translations";

interface WorkflowLibraryCardProps {
  workflows: Workflow[];
  onSelect: (workflow: Workflow) => void;
}

export function WorkflowLibraryCard({ workflows, onSelect }: WorkflowLibraryCardProps) {
  const intl = useIntl();
  const { limit, offset, page, setPage, setPageSize, searchString, setSearchString } = useTableData(
    {
      initialPage: 1,
      initialPageSize: 10,
    }
  );
  const icon = useIconSrc("job");

  const filteredWorkflows = workflows.filter((workflow) =>
    workflow.title.toLowerCase().includes(searchString.toLowerCase())
  );
  const numPages = Math.ceil(filteredWorkflows.length / limit);
  const visibleWorkflows = filteredWorkflows.slice(offset, offset + limit);

  return (
    <LibraryCard
      title={intl.formatMessage({
        id: "heading.workflow_library",
        defaultMessage: "Workflow library",
      })}
      description={
        <FormattedMessage
          id="workflowlist.click_here_to_see_all_workflows"
          defaultMessage="Click here to see all workflows"
        />
      }
      buttonLabel={<FormattedMessage id="workflowlist.library" defaultMessage="Library" />}
      modalTitle={intl.formatMessage({
        id: "workflowlist.all_workflows",
        defaultMessage: "All workflows",
      })}
      height={SMALL_TILE_CARD_HEIGHT}
    >
      <RanaTable
        columnDefinitions={[
          {
            width: "table_cell_sm",
            title: nameColumnString(intl),
          },
          {
            width: "table_cell",
            title: intl.formatMessage({
              id: "column.description",
              defaultMessage: "Description",
            }),
          },
        ]}
        tableDataIds={visibleWorkflows.map((workflow) => workflow.id)}
        emptyElement={
          <RanaTable.Empty icon={icon}>
            <FormattedMessage
              id="emptyElement.workflow_no_search_results"
              defaultMessage="No workflows found with the current search criteria."
            />
          </RanaTable.Empty>
        }
      >
        <RanaTable.Filters
          search={{
            placeholder: intl.formatMessage({
              id: "search.workflow",
              defaultMessage: "Find workflow",
            }),
            searchString,
            setSearchString,
          }}
        />
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {visibleWorkflows.map((workflow) => {
              const { id, subtitle, title } = workflow;
              return (
                <RanaTable.Row onClick={() => onSelect(workflow)} key={id} rowId={id}>
                  <RanaTable.Cell>{title}</RanaTable.Cell>
                  <RanaTable.Cell title={subtitle}>{subtitle}</RanaTable.Cell>
                </RanaTable.Row>
              );
            })}
          </RanaTable.Body>
        </RanaTable.Container>

        <Collapse in={numPages > 1} unmountOnExit>
          <RanaTable.Pagination
            limit={limit}
            setPageSize={setPageSize}
            page={page}
            numPages={numPages}
            setPage={setPage}
          />
        </Collapse>
      </RanaTable>
    </LibraryCard>
  );
}
